import { useState, useCallback, useEffect } from "react";
import {
  _get_available_clusters,
  _accept_delivery_cluster,
} from "../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../common/utils/notification";

const useClusterAllocation = (bikerVehicleType = "car") => {
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [clusterHistory, setClusterHistory] = useState([]);

  const getVehicleCategory = (vehicleType) => {
    const categoryMap = {
      BIKE: "bike",
      CAR: "car",
      VAN: "van",
    };
    return categoryMap[vehicleType?.toUpperCase()] || "bike";
  };

  const filterClusters = useCallback(
    (allClusters) => {
      const vehicleCategory = getVehicleCategory(bikerVehicleType);

      if (!["car", "van"].includes(vehicleCategory)) {
        return [];
      }

      return allClusters
        .filter((cluster) => {
          if (!cluster.orders || cluster.orders.length === 0) {
            return false;
          }

          if (!cluster.destinations || cluster.destinations.length === 0) {
            return false;
          }

          if (vehicleCategory === "car" && cluster.totalStops > 4) {
            return false;
          }

          return true;
        })
        .sort((a, b) => {
          const dateA = new Date(a.deliveryDate);
          const dateB = new Date(b.deliveryDate);
          return dateA - dateB;
        });
    },
    [bikerVehicleType]
  );

  const filteredClusters = filterClusters(clusters);

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await _get_available_clusters();

      if (response.success && response.data?.data) {
        setClusters(response.data.data);

        if (!selectedCluster && response.data.data.length > 0) {
          setSelectedCluster(response.data.data[0]);
        }
      } else {
        setError("Failed to load available clusters");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch clusters";
      setError(errorMsg);
      showErrorNotification("Error", errorMsg);
    } finally {
      setLoading(false);
    }
  }, [selectedCluster]);

  const selectCluster = useCallback((cluster) => {
    setSelectedCluster(cluster);
    setError(null);
  }, []);

  const acceptCluster = useCallback(
    async (clusterId) => {
      setAccepting(true);
      setError(null);

      try {
        const response = await _accept_delivery_cluster(clusterId);

        if (response.success) {
          const acceptedCluster = selectedCluster;
          setClusterHistory((prev) => [
            ...prev,
            {
              ...acceptedCluster,
              acceptedAt: new Date(),
            },
          ]);

          setClusters((prev) =>
            prev.filter((c) => c.id !== clusterId)
          );

          setSelectedCluster(null);

          showSuccessNotification(
            "Success",
            `Cluster ${acceptedCluster?.clusterId} accepted! You now have ${acceptedCluster?.totalOrders} deliveries.`
          );

          setAccepting(false);
          return response;
        }
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;

        if (status === 409) {
          setError(
            "This cluster was just accepted by another delivery! Refreshing available clusters..."
          );

          setTimeout(() => {
            fetchClusters();
          }, 1000);

          showErrorNotification(
            "Cluster Taken",
            "Another delivery accepted this cluster first. Check other available clusters."
          );
        } else if (status === 400) {
          setError(message || "Invalid cluster or invalid state");
          showErrorNotification("Invalid Request", message);
        } else {
          setError(message || "Failed to accept cluster");
          showErrorNotification("Error", message);
        }

        setAccepting(false);
        throw err;
      }
    },
    [selectedCluster, fetchClusters]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const vehicleCategory = getVehicleCategory(bikerVehicleType);
  const isEligible = ["car", "van"].includes(vehicleCategory);

  const getCapacityInfo = () => {
    if (vehicleCategory === "car") {
      return {
        minStops: 2,
        maxStops: 4,
        description: "2-4 delivery stops",
      };
    } else if (vehicleCategory === "van") {
      return {
        minStops: 5,
        maxStops: 20,
        description: "5+ delivery stops",
      };
    }
    return {
      minStops: 0,
      maxStops: 0,
      description: "Not eligible",
    };
  };

  useEffect(() => {
    if (isEligible) {
      fetchClusters();
    }
  }, [isEligible, fetchClusters]);

  return {
    clusters,
    filteredClusters,
    selectedCluster,
    loading,
    accepting,
    error,
    clusterHistory,
    vehicleCategory,
    isEligible,
    capacityInfo: getCapacityInfo(),
    fetchClusters,
    selectCluster,
    acceptCluster,
    clearError,
  };
};

export default useClusterAllocation;
